import { action, makeObservable, observable, runInAction, set } from 'mobx';
import { debounce } from 'throttle-debounce';

import { SPLIT_RANGE_DEPTH, SPLIT_RANGE_TIME_IN_SECONDS } from 'src/api/consts';
import { OperationAbortedError } from 'src/app/errors';
import { requireService } from 'src/packages/di';

import { storage } from '../shared/consts/storage';
import { equalsDeep } from '../shared/utils/equalsDeep';

import type { IUserPreferenceService } from './IUserPreferenceService';
import type { TUserPreference, UserSelectedDepthIndex } from './UserPreference.api';
import { UserPreferenceApi } from './UserPreference.api';

export class UserPreferenceService implements IUserPreferenceService {
  private readonly api: UserPreferenceApi;

  @observable preferences: TUserPreference;

  syncedPreferences: TUserPreference;

  @observable preferencesId: number | null = null;

  @observable isLoading = false;
  @observable isInitialized = false;

  abortController: AbortController | null = null;

  debounceSyncPreferences = debounce(300, this.syncPreferences);

  constructor(
    private readonly notifications = requireService('notifications'),
    private readonly auth = requireService('authService'),
  ) {
    this.api = new UserPreferenceApi(auth);
    this.preferences = this.getDefaultPreferences();
    this.syncedPreferences = this.getDefaultPreferences();
    makeObservable(this);
  }

  async loadPreferences() {
    this.setLoading(true);
    try {
      const preferences = await this.api.loadPreferences();
      if (preferences.length === 0) {
        const emptyPreferences: TUserPreference = this.getDefaultPreferences();
        const newPreferences = await this.api.createPreferences(emptyPreferences);
        runInAction(() => {
          this.preferences = emptyPreferences;
          this.preferencesId = newPreferences.id;
          this.syncedPreferences = emptyPreferences;
        });
        return;
      }
      runInAction(() => {
        this.preferences = preferences[0].data;
        this.preferencesId = preferences[0].id;
        this.syncedPreferences = preferences[0].data;
        this.addDefaultPreferences();
      });
    } catch (error) {
      this.notifications.showErrorMessageT('errors:failedToLoadUserPreferences');
      console.error(error);
    } finally {
      this.setLoading(false);
      runInAction(() => {
        this.isInitialized = true;
      });
    }
  }

  async updatePreferences(preferences: Partial<Exclude<TUserPreference, 'ownerUserId'>>) {
    set(this.preferences, preferences);
    this.debounceSyncPreferences();
  }

  async syncPreferences() {
    if (equalsDeep(this.preferences, this.syncedPreferences)) {
      return;
    }
    if (!this.preferencesId) {
      throw new Error('Preferences ID is not set');
    }
    if (this.isLoading && this.abortController && !this.abortController.signal.aborted) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const clonedPreferences = { ...this.preferences };
    this.setLoading(true);
    try {
      await this.api.updatePreferences(this.preferencesId, clonedPreferences, this.abortController);
      runInAction(() => {
        this.setLoading(false);
        this.abortController = null;
        this.syncedPreferences = clonedPreferences;
      });
    } catch (error) {
      console.error(error);
      if (error instanceof OperationAbortedError) {
        return;
      }
      this.notifications.showErrorMessageT('errors:failedToUpdateUserPreferences');
      runInAction(() => {
        this.preferences = this.syncedPreferences;
        this.setLoading(false);
      });
    }
  }

  @action
  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  equals(preferences: TUserPreference) {
    return equalsDeep(this.preferences, preferences);
  }

  getDefaultPreferences(): TUserPreference {
    return {
      ownerUserId: this.auth.userInfo.sub,
      locationNameConfigurator: {
        block: { order: null, displayName: 'abbreviatedName' },
        field: { order: 1, displayName: 'abbreviatedName' },
        pad: { order: 2, displayName: 'abbreviatedName' },
      },
      autoSelectWellboreForTime: 'main',
      autoSelectWellboreForDepth: 'active',
      // TODO: remove localStorage default values after 3 months after release
      isAltNames: storage.GET<boolean>('altNames') ?? true,
      isColoredParamsHeaders: storage.GET<boolean>('coloredParamsHeaders') ?? false,
      defaultDepthUnits: storage.GET<UserSelectedDepthIndex>('defaultDepthUnits') ?? 'INDEX_DEPTH_SCALE_METERS',
      defaultSplitRange: {
        time: Number(SPLIT_RANGE_TIME_IN_SECONDS),
        depth: Number(SPLIT_RANGE_DEPTH),
      },
      favoriteReferenceGroupIds: [],
      systemDatumId: null,
      azimuthReference: 'TRUE',
    };
  }

  addDefaultPreferences() {
    this.preferences = { ...this.getDefaultPreferences(), ...this.preferences };
    this.syncedPreferences = { ...this.getDefaultPreferences(), ...this.preferences };
  }
}
